/* eslint-disable react/no-find-dom-node */
import PropTypes from 'prop-types'
import { Component, Fragment, createRef } from 'react'
import ReactDOM from 'react-dom'
import listen from 'simple-listen'

import { isNumeric } from 'support/helpers/utilities'

export default class Popover extends Component {
  static propTypes = {
    alignPopover: PropTypes.string,
    arrowSize: PropTypes.number,
    /*
      Close when a click occurs inside the popover.

      default: false
     */
    closeOnInnerClick: PropTypes.bool,
    disabled: PropTypes.bool,
    edgeBuffer: PropTypes.number,
    hoverDelay: PropTypes.number,
    /*
      Avoid closing the popover because of a click on an element outside of
      the popover or its trigger by adding an html attribute (e.g. data-no-popover-close)
      to that element and supplying that attr to the ignoreOutsideClickElementAttr prop
      as a string.

      ignoreOutsideClickElementAttr="data-no-popover-close"

     */
    ignoreOutsideClickElementAttr: PropTypes.string,
    /*
      Open popover on initial mount

      default: false
     */
    openOnMount: PropTypes.bool,
    openOnMountDelay: PropTypes.number,
    openOnClick: PropTypes.bool,
    onClose: PropTypes.func,
    onOpen: PropTypes.func,
    onMouseEnter: PropTypes.func,
    onMouseLeave: PropTypes.func,
    onClick: PropTypes.func,
    onTouchStart: PropTypes.func,
    onTouchEnd: PropTypes.func,
    children: PropTypes.func,
    topBuffer: PropTypes.number,
    transitionOpening: PropTypes.bool,
    forceMeasureProps: PropTypes.array,
    override: PropTypes.number
  }

  static defaultProps = {
    alignPopover: Popover.ALIGN_CENTER,
    arrowSize: 8,
    closeOnInnerClick: false,
    disabled: false,
    edgeBuffer: 8,
    hoverDelay: 300,
    ignoreOutsideClickElementAttr: 'data-no-popover-close',
    onClose: () => {},
    onOpen: () => {},
    openOnMount: false,
    openOnMountDelay: 0,
    openOnClick: false,
    topBuffer: 64,
    transitionOpening: false,
    forceMeasureProps: []
  }

  state = {
    open: false,
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    height: 0,
    width: 0,
    tipLeft: 0,
    tipHeight: 0,
    tipWidth: 0,
    initialTipMeasurementTaken: false
  }

  documentListener = null
  scrollListener = null
  tipMouseEnterListener = null
  tipMouseLeaveListener = null
  ignoreMouseOver = null
  overTimeout = null
  outTimeout = null
  innerClickTimeout = null
  prevTop = null
  prevLeft = null
  prevArrowLeft = null
  prevDirection = null
  setNextStateAnimationId = null
  measureTimeout = null

  componentDidMount() {
    if (this.props.openOnMount) {
      this.measureAndOpen()
    }
  }

  getSnapshotBeforeUpdate(prevProps, prevState) {
    if (prevState.open === false && this.state.open === true) {
      this.attachEventListeners()
      const nextState = this.measureTip()
      if (
        !Object.keys(nextState).every((k) => nextState[k] === this.state[k])
      ) {
        return nextState
      }
    } else if (prevState.open === true && this.state.open === false) {
      this.detachEventListeners()
    }

    return null
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (snapshot) {
      this.setState(snapshot)
    } else if (
      prevState.open &&
      this.state.open &&
      this.dependentPropsDiff(prevProps)
    ) {
      setTimeout(() => {
        this.setState(this.measureTip())
      }, 0)
    }
  }

  componentWillUnmount() {
    if (this.state.open) {
      this.props.onClose()
    }
    clearTimeout(this.overTimeout)
    clearTimeout(this.outTimeout)
    clearTimeout(this.innerClickTimeout)
    clearTimeout(this.measureTimeout)
    window.cancelAnimationFrame(this.setNextStateAnimationId)
    this.detachEventListeners()
  }

  targetRef = createRef()

  tipRef = createRef()

  dependentPropsDiff(prevProps) {
    if (
      !this.props.forceMeasureProps.length &&
      !prevProps.forceMeasureProps.length
    ) {
      return false
    }
    return this.props.forceMeasureProps.some((val, i) => {
      return val !== prevProps.forceMeasureProps[i]
    })
  }

  render() {
    const { open, initialTipMeasurementTaken } = this.state
    const { children, transitionOpening } = this.props

    // arrowLeft, direction will be to position arrow on tooltips in the future
    // eslint-disable-next-line no-unused-vars
    const { arrowLeft, direction, ...popoverStyle } = this.getStyle()

    return (
      <Fragment>
        {children({
          open,
          openPopover: this.measureAndOpen,
          closePopover: this.closePopover,
          getTargetProps: (targetProps = {}) => {
            return Object.assign(
              {},
              {
                key: 'rendered-target',
                ref: (el) => {
                  this.wrapper = el
                },
                onMouseEnter: this.handleMouseEnter,
                onMouseLeave: this.handleMouseLeave,
                onClick: this.handleClick,
                onTouchStart: this.handleTouchStart,
                onTouchEnd: this.handleTouchEnd
              },
              targetProps,
              {
                css: [
                  {
                    position: 'relative',
                    zIndex: 0
                  },
                  targetProps.css
                ]
              }
            )
          },
          getPopoverProps: (popoverProps = {}) => {
            return Object.assign(
              {},
              {
                key: 'rendered-popover',
                ref: (el) => {
                  this.tip = el
                },
                popoverOpen: open,
                arrowLeft,
                arrowDirection: direction
              },
              popoverProps,
              {
                css: [
                  {
                    position: 'fixed',
                    opacity: initialTipMeasurementTaken
                      ? popoverStyle.opacity
                      : 0,
                    pointerEvents: open ? 'initial' : 'none',
                    transition: transitionOpening
                      ? 'opacity 500ms ease-in'
                      : 'none'
                  },
                  popoverProps.css,
                  popoverStyle
                ]
              }
            )
          }
        })}
      </Fragment>
    )
  }

  handleClick = (e) => {
    if (e.target.tagName === 'A') {
      return
    }

    if (this.props.onClick) {
      this.props.onClick(e)
    }

    if (!this.props.openOnClick) {
      return
    }

    const tipEl = ReactDOM.findDOMNode(this.tip)

    if (tipEl && (e.target === tipEl || tipEl.contains(e.target))) {
      return
    }

    if (this.state.open) {
      this.closePopover()
    } else {
      this.measureAndOpen()
    }
  }

  handleDocumentClick = (e) => {
    const tipEl = ReactDOM.findDOMNode(this.tip)

    if (!this.state.open) {
      return
    }

    const wrapperEl = ReactDOM.findDOMNode(this.wrapper)
    if (e.target === wrapperEl || wrapperEl.contains(e.target)) {
      return
    }

    const { ignoreOutsideClickElementAttr } = this.props

    if (ignoreOutsideClickElementAttr) {
      let node = e.target
      let hasIgnoreAttr = false

      while (node) {
        hasIgnoreAttr = node?.attributes?.[ignoreOutsideClickElementAttr]

        if (hasIgnoreAttr) {
          return
        }
        node = node.parentNode
      }
    }

    if (
      this.props.closeOnInnerClick ||
      ['BUTTON', 'A'].includes(e.target.tagName)
    ) {
      this.innerClickTimeout = setTimeout(this.closePopover, 16)
      return
    } else if (tipEl && tipEl.contains(e.target)) {
      return
    }

    this.closePopover()
  }

  handleMouseEnter = (e, fromTipEventHandler) => {
    if (!fromTipEventHandler && this.props.onMouseEnter) {
      this.props.onMouseEnter(e)
    }

    if (this.props.openOnClick || this.ignoreMouseOver) {
      return
    }

    clearTimeout(this.outTimeout)
    this.outTimeout = null

    if (this.overTimeout) {
      return
    }

    this.overTimeout = setTimeout(() => {
      this.overTimeout = null
      this.measureAndOpen()
    }, this.props.hoverDelay)
  }

  handleMouseLeave = (e, fromTipEventHandler) => {
    if (!fromTipEventHandler && this.props.onMouseLeave) {
      this.props.onMouseLeave(e)
    }

    if (this.props.openOnClick) {
      return
    }

    clearTimeout(this.overTimeout)
    this.overTimeout = null

    if (this.outTimeout) {
      return
    }

    if (!this.state.open) {
      return
    }

    this.outTimeout = setTimeout(() => {
      this.outTimeout = null
      this.closePopover()
    }, this.props.hoverDelay / 2)
  }

  handleTouchStart = (e) => {
    if (this.props.onTouchStart) {
      this.props.onTouchStart(e)
    }

    if (!this.props.openOnClick) {
      this.ignoreMouseOver = true
    }
  }

  handleTouchEnd = (e) => {
    if (this.props.onTouchEnd) {
      this.props.onTouchEnd(e)
    }

    this.ignoreMouseOver = false
  }

  handleResize = () => {
    if (this.setNextStateAnimationId) {
      window.cancelAnimationFrame(this.setNextStateAnimationId)
    }

    this.setNextStateAnimationId = window.requestAnimationFrame(() => {
      this.setNextStateAnimationId = null
      this.setState(this.measure())
    })
  }

  attachEventListeners() {
    if (this.props.openOnClick) {
      this.documentListener = listen(
        document,
        'click',
        this.handleDocumentClick,
        true
      )
    }

    const tipEl = ReactDOM.findDOMNode(this.tip)

    if (tipEl) {
      this.tipMouseEnterListener = listen(tipEl, 'mouseenter', (e) => {
        this.handleMouseEnter(e, true)
      })

      this.tipMouseLeaveListener = listen(tipEl, 'mouseleave', (e) => {
        this.handleMouseLeave(e, true)
      })
    }

    this.resizeListener = listen(window, 'resize', this.handleResize)
    this.scrollListener = listen(window, 'scroll', () => {
      this.setState({ open: false }, this.scrollListener)
    })
  }

  detachEventListeners() {
    window.cancelAnimationFrame(this.setNextStateAnimationId)

    const listeners = [
      this.documentListener,
      this.scrollListener,
      this.resizeListener,
      this.tipMouseEnterListener,
      this.tipMouseLeaveListener
    ]

    listeners.forEach((listener) => {
      if (listener && typeof listener === 'function') {
        listener()
      }
    })
  }

  measureAndOpen = () => {
    if (this.props.disabled) {
      return
    }

    this.measureTimeout = setTimeout(() => {
      this.setState({ open: true, ...this.measure() }, this.props.onOpen)
    }, this.props.openOnMountDelay || 0)
  }

  closePopover = () => {
    if (this.state.open === false) {
      return
    }

    this.setState({ open: false }, this.props.onClose)
  }

  measure = () => {
    const wrapperEl = ReactDOM.findDOMNode(this.wrapper)
    const rect = wrapperEl?.getBoundingClientRect() ?? {}

    /**
     * If this is being positioned within a component where it injects
     * additional divs within itself (i.e AutoSizer), it can mess
     * up the measurement calculation. This allows for the parent component to pass
     * down an override to control the `top` value
     */
    const validOverride = isNumeric(this.props.override)

    const measurement = {
      top: validOverride ? this.props.override : rect.top,
      right: rect.right,
      bottom: rect.bottom,
      left: validOverride ? wrapperEl?.offsetLeft : rect.left,
      height: rect.height,
      width: rect.width,
      bodyWidth: document.body.clientWidth
    }

    /**
     * If this is being positioned within an OffCanvas component,
     * we need to measure the offset based on the OffCanvas, not the window.
     */

    const container = wrapperEl.closest('.OffCanvas__content')

    if (container) {
      const containerRect = container.getBoundingClientRect()
      measurement.top -= containerRect.top
      measurement.left -= containerRect.left

      // also account for the vertical scroll position of the OffCanvas
      // otherwise, the tooltip will show further up the screen instead of directly above it
      measurement.top += container.scrollTop
    }

    return measurement
  }

  measureTip() {
    const tipEl = ReactDOM.findDOMNode(this.tip)
    const tipRect = tipEl
      ? tipEl.getBoundingClientRect()
      : { left: 0, width: 0, height: 0 }

    return {
      tipLeft: tipRect.left,
      tipWidth: tipRect.width,
      tipHeight: tipRect.height,
      initialTipMeasurementTaken: true
    }
  }

  getStyle = () => {
    const { open } = this.state
    const { alignPopover, arrowSize, edgeBuffer, topBuffer } = this.props

    if (!open) {
      return {
        top: this.prevTop || 0,
        left: this.prevLeft || 0,
        opacity: 0,
        arrowLeft: this.prevArrowLeft || 0,
        direction: this.prevDirection || 'top'
      }
    }

    const {
      height: parentHeight,
      top: parentTop,
      left: parentLeft,
      right: parentRight,
      width: parentWidth,
      bodyWidth,
      tipWidth,
      tipHeight
    } = this.state

    const halfParentWidth = parentWidth / 2
    let direction = 'top'
    let topPos = 0 - tipHeight - arrowSize
    let leftPos
    let arrowLeft

    switch (alignPopover) {
      case Popover.ALIGN_LEFT:
        leftPos = 0
        // The arrow shouldn't be less than its width from the edge
        arrowLeft = 0
        break

      case Popover.ALIGN_RIGHT:
        leftPos = parentWidth - tipWidth
        // The arrow shouldn't be less than its width from the edge
        arrowLeft = Math.max(tipWidth - halfParentWidth, 3)
        break

      case Popover.ALIGN_CENTER:
      default:
        leftPos = halfParentWidth - tipWidth / 2
        arrowLeft = 0.5 * tipWidth
        break
    }

    // Too far left
    if (parentLeft + leftPos < edgeBuffer) {
      leftPos = 0 - (parentLeft - edgeBuffer)
      arrowLeft = halfParentWidth - leftPos
    }

    // Check if too far right
    const rightPos = leftPos + tipWidth
    const rightEdge = bodyWidth - edgeBuffer

    if (parentLeft + rightPos > rightEdge) {
      const distancePastRightEdge = parentRight + rightPos - rightEdge
      leftPos = leftPos + parentWidth - distancePastRightEdge
      arrowLeft = halfParentWidth - leftPos
    }

    // Check if too far up
    if (parentTop + topPos < topBuffer || alignPopover === 'bottom') {
      topPos = parentHeight + arrowSize
      direction = 'bottom'
    }

    const top = parentTop + topPos
    const left = parentLeft + leftPos

    this.prevTop = top
    this.prevLeft = left
    this.prevArrowLeft = arrowLeft
    this.prevDirection = direction

    return {
      top,
      left,
      opacity: 1,
      arrowLeft,
      direction
    }
  }
}

Popover.ALIGN_CENTER = 'center'
Popover.ALIGN_LEFT = 'left'
Popover.ALIGN_RIGHT = 'right'
Popover.ALIGN_BOTTOM = 'bottom'
